"use client";

import { memo, useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { usePdfDocumentStore } from "./pdfDocumentStore";
import { usePdfViewerContext } from "./PdfViewerCore";
import { ClientLogger } from "@/lib/clientLogger";

const THUMB_SCALE = 0.2;

type ThumbEntry = {
  pageNum: number;
  rendered: boolean;
};

export const PdfThumbnailBar = memo(function PdfThumbnailBar() {
  const activeDocumentId = usePdfDocumentStore((s) => s.activeDocumentId);
  const documents = usePdfDocumentStore((s) => s.documents);
  const activeDoc = documents.find((d) => d.id === activeDocumentId);
  const { instanceRef, isReady } = usePdfViewerContext();

  const [currentPage, setCurrentPage] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const renderedSet = useRef<Set<number>>(new Set());

  // Track current page from viewer events.
  useEffect(() => {
    if (!isReady) return;
    const eb = instanceRef.current?.eventBus;
    if (!eb) return;
    const onPage = (e: { pageNumber: number }) => setCurrentPage(e.pageNumber);
    eb.on("pagechanging", onPage);
    return () => eb.off("pagechanging", onPage);
  }, [isReady, instanceRef]);

  // Reset when document changes.
  useEffect(() => {
    renderedSet.current.clear();
    setCurrentPage(1);
  }, [activeDocumentId]);

  // Render a thumbnail for a given page.
  const renderThumb = useCallback(
    async (pageNum: number) => {
      if (!activeDoc || renderedSet.current.has(pageNum)) return;
      const canvas = canvasRefs.current.get(pageNum);
      if (!canvas) return;
      if (!activeDoc.pdfDocumentProxy) return;

      renderedSet.current.add(pageNum);

      try {
        const page = await activeDoc.pdfDocumentProxy.getPage(pageNum);
        const viewport = page.getViewport({ scale: THUMB_SCALE });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        await page.render({ canvas, canvasContext: ctx, viewport }).promise;
      } catch (err) {
        ClientLogger.debug("Thumbnail render failed", {
          error: err instanceof Error ? err : undefined, component: "PdfThumbnailBar",
        });
        renderedSet.current.delete(pageNum);
      }
    },
    [activeDoc],
  );

  // IntersectionObserver to lazy-render only visible thumbnails.
  useEffect(() => {
    if (!activeDoc) return;
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const pageNum = Number(
              (entry.target as HTMLElement).dataset.pageNum,
            );
            if (pageNum) renderThumb(pageNum);
          }
        }
      },
      { root: container, rootMargin: "100px" },
    );

    const wrappers = container.querySelectorAll("[data-page-num]");
    for (const el of wrappers) observer.observe(el);

    return () => observer.disconnect();
  }, [activeDoc, renderThumb]);

  const goToPage = useCallback(
    (pageNum: number) => {
      const viewer = instanceRef.current?.viewer;
      if (viewer) viewer.currentPageNumber = pageNum;
    },
    [instanceRef],
  );

  if (!activeDoc) return null;

  const pages = Array.from({ length: activeDoc.pageCount ?? 1 }, (_, i) => i + 1);

  return (
    <div
      ref={containerRef}
      className="flex h-full flex-col gap-2 overflow-y-auto p-2"
      style={{ scrollbarWidth: "thin" }}
    >
      {pages.map((pageNum) => (
        <button
          key={pageNum}
          type="button"
          data-page-num={pageNum}
          onClick={() => goToPage(pageNum)}
          className={cn(
            "group relative flex flex-col items-center rounded border transition-colors",
            currentPage === pageNum
              ? "border-primary bg-primary/5"
              : "border-border/50 hover:border-border",
          )}
        >
          <canvas
            ref={(el) => {
              if (el) canvasRefs.current.set(pageNum, el);
              else canvasRefs.current.delete(pageNum);
            }}
            className="w-full"
          />
          <span className="py-0.5 text-[10px] tabular-nums text-muted-foreground">
            {pageNum}
          </span>
        </button>
      ))}
    </div>
  );
});
